import { Egg, HeartHandshake, Network, Snowflake, TestTubeDiagonal } from "lucide-react";
import { Link } from "react-router";
import { t } from "../../web/lib/i18n";
import { defineWebModule } from "../../web/module";
import { Icon } from "../../web/ui/kit";
import { showGenetics, GeneticsSection } from "./web/AnimalSection";
import { DnaOrderJob } from "./web/DnaPage";
import { CompositionForm, EvaluationForm, PedigreeCorrectionForm } from "./web/forms";
import { GeneticsWorkspace } from "./web/GeneticsWorkspace";
import { GeneticInventory } from "./web/Inventory";
import { MatingPlanJob } from "./web/Mating";
import { RegistrationJob, RegistrationWorkspace } from "./web/Registration";
import { GeneticsSettings } from "./web/Settings";
import "./web/genetics.css";

function StockLink({ entity }: { entity: Record<string, any> }) {
  return (
    <Link to={`/ranch/genetic-inventory/${entity.id}`} className="link"><Icon name="tank" size={16} />{t("Open in Semen and embryos for tanks, holds and history")}</Link>
  );
}

const registered = (a: { facets: Record<string, unknown>; identifiers: Array<{ scheme: string; valid_to: string | null }> }) =>
  a.identifiers.some((i) => i.scheme === "registration" && !i.valid_to) || (a.facets.genetics as { registration?: { status?: string } } | undefined)?.registration?.status === "registered";

export default defineWebModule({
  id: "genetics",
  icons: { pedigree: Network, tank: Snowflake, straw: TestTubeDiagonal, embryo: Egg, mating: HeartHandshake },
  animalSections: [{ id: "genetics", label: "Genetics", order: 45, show: showGenetics, Component: GeneticsSection }],
  animalActions: [
    { id: "genetics-dna", label: "DNA sample", icon: "dna", eventType: "genetics.dna_sample", data: () => ({ sample_type: "hair" }), when: (a) => a.status === "active", priority: 14 },
    {
      id: "genetics-register", label: "Register", icon: "award", priority: 13,
      when: (a) => a.status === "active" && !registered(a) && !(a.sex === "male" && a.castrated) && (a.age_days ?? 9999) < 730,
      run: (a, navigate) => navigate(`/work/registration/new?season=${a.birth_date?.slice(0, 4) ?? ""}&animal=${a.id}`),
    },
    { id: "genetics-epds", label: "Record EPDs", icon: "report", eventType: "genetics.evaluation", priority: 12 },
    { id: "genetics-collect", label: "Collect semen", icon: "straw", priority: 6, when: (a) => a.sex === "male" && !a.castrated && a.class === "bull" && a.status === "active", run: (a, navigate) => navigate(`/ranch/genetic-inventory?do=collect&animal=${a.id}`) },
    { id: "genetics-flush", label: "Record a flush", icon: "embryo", priority: 5, when: (a) => a.sex === "female" && a.class === "cow" && a.status === "active", run: (a, navigate) => navigate(`/ranch/genetic-inventory?do=flush&animal=${a.id}`) },
    { id: "genetics-pedigree", label: "Correct pedigree", icon: "pedigree", eventType: "genetics.pedigree_correction", data: () => ({ role: "sire" }), priority: 4 },
  ],
  workspaces: { genetics: GeneticsWorkspace, registration: RegistrationWorkspace },
  ranchSections: { "genetic-inventory": GeneticInventory },
  jobs: { "genetics.registration": RegistrationJob, "genetics.mating_plan": MatingPlanJob, "genetics.dna_order": DnaOrderJob },
  events: {
    "genetics.evaluation": { Form: EvaluationForm },
    "genetics.breed_composition": { Form: CompositionForm },
    "genetics.pedigree_correction": { Form: PedigreeCorrectionForm },
  },
  stockSections: [{ id: "genetics", label: "Semen and embryos", order: 10, show: (e) => e.kind === "semen" || e.kind === "embryo", Component: StockLink }],
  Settings: GeneticsSettings,
});
